"use client"

import * as React from "react"
import * as PopoverPrimitive from "@radix-ui/react-popover"

import { cn } from "@/lib/utils"

const PopoverContext = React.createContext<{
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  onMouseEnter: () => void
  onMouseLeave: () => void
} | undefined>(undefined)

const Popover = ({
  children,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  defaultOpen,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Root>) => {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen ?? false)
  const open = controlledOpen ?? uncontrolledOpen
  const timerRef = React.useRef<NodeJS.Timeout | null>(null)
  const isMouseLeaveCloseRef = React.useRef(false)
  const openRef = React.useRef(open)

  const handleOpenChange = React.useCallback(
    (newOpen: boolean) => {
      if (controlledOnOpenChange) {
        controlledOnOpenChange(newOpen)
      } else {
        setUncontrolledOpen(newOpen)
      }
    },
    [controlledOnOpenChange]
  )

  const setOpen = React.useCallback(
    (newOpen: boolean | ((prev: boolean) => boolean)) => {
      if (typeof newOpen === "function") {
        const currentOpen = controlledOpen ?? uncontrolledOpen
        const result = newOpen(currentOpen)
        handleOpenChange(result)
      } else {
        handleOpenChange(newOpen)
      }
    },
    [controlledOpen, uncontrolledOpen, handleOpenChange]
  )

  React.useEffect(() => {
    openRef.current = open
  }, [open])

  const onMouseEnter = React.useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    isMouseLeaveCloseRef.current = false
    if (!openRef.current) {
      handleOpenChange(true)
    }
  }, [handleOpenChange])

  const onMouseLeave = React.useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }
    isMouseLeaveCloseRef.current = true
    // 增加延迟时间，给用户更多时间从 trigger 移动到 content
    timerRef.current = setTimeout(() => {
      setOpen(false)
      timerRef.current = null
    }, 300)
  }, [setOpen])

  const handleRootOpenChange = React.useCallback(
    (newOpen: boolean) => {
      if (newOpen) {
        handleOpenChange(true)
      } else if (isMouseLeaveCloseRef.current) {
        handleOpenChange(false)
      }
    },
    [handleOpenChange]
  )

  React.useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [])

  return (
    <PopoverContext.Provider value={{ open, setOpen, onMouseEnter, onMouseLeave }}>
      <PopoverPrimitive.Root open={open} onOpenChange={handleRootOpenChange} {...props}>
        {children}
      </PopoverPrimitive.Root>
    </PopoverContext.Provider>
  )
}

type PopoverTriggerProps = React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Trigger> & {
  openOnClick?: boolean
  openOnHover?: boolean
}

const PopoverTrigger = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Trigger>,
  PopoverTriggerProps
>(({ className, onClick, onMouseEnter, onMouseLeave, openOnClick = true, openOnHover = true, ...props }, ref) => {
  const context = React.useContext(PopoverContext)

  return (
    <PopoverPrimitive.Trigger
      ref={ref}
      className={className}
      onClick={(e) => {
        if (context && openOnClick) {
          e.preventDefault()
          e.stopPropagation()
          context.onMouseEnter()
          context.setOpen(true)
        }
        onClick?.(e)
      }}
      onMouseEnter={(e) => {
        if (openOnHover) {
          context?.onMouseEnter()
        }
        onMouseEnter?.(e)
      }}
      onMouseLeave={(e) => {
        if (openOnHover) {
          context?.onMouseLeave()
        }
        onMouseLeave?.(e)
      }}
      {...props}
    />
  )
})
PopoverTrigger.displayName = PopoverPrimitive.Trigger.displayName

const PopoverAnchor = PopoverPrimitive.Anchor

const PopoverArrow = PopoverPrimitive.Arrow

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ className, align = "center", sideOffset = 4, onMouseEnter, onMouseLeave, ...props }, ref) => {
  const context = React.useContext(PopoverContext)

  return (
    <PopoverPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      className={cn(
        "z-50 w-72 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className
      )}
      onMouseEnter={(e) => {
        context?.onMouseEnter()
        onMouseEnter?.(e)
      }}
      onMouseLeave={(e) => {
        context?.onMouseLeave()
        onMouseLeave?.(e)
      }}
      {...props}
    />
  )
})
PopoverContent.displayName = PopoverPrimitive.Content.displayName

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor, PopoverArrow }
